import { cva, type VariantProps } from 'class-variance-authority';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { type ComponentProps, type FC } from 'react';
import { cn } from '../lib/utils';
import { usePortalContainer } from './PortalContainer';
import { Tooltip, TooltipProvider, TooltipTrigger } from './ui/tooltip';

// Re-exported unchanged — only Content gets a variant.
export { Tooltip, TooltipProvider, TooltipTrigger };

// Positioning + enter/exit motion — copied from the generated `ui/tooltip.tsx` TooltipContent,
// not part of the Figma spec. We can't render that component directly and just override its
// className: its Arrow is baked in with a fixed dark color, and we need the Arrow to follow
// `variant` below. Kept as its own constant so the cva base underneath only lists the actual
// Figma-driven bits, and so this is easy to diff against `ui/tooltip.tsx` if that regenerates.
const radixContentMotionClasses =
  'z-50 w-fit origin-(--radix-tooltip-content-transform-origin) text-balance animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2';

const tooltipContentVariants = cva(
  cn(
    radixContentMotionClasses,
    // `text-wrap` replaces the generated `text-balance`. The box sizes to the unbalanced
    // text first, so balanced lines leave empty space on both sides of a wrapped tooltip.
    'max-w-64 rounded-[4px] px-3 py-2 label-sm text-center text-wrap',
  ),
  {
    variants: {
      // All four render the same Figma "Tooltip" surface (7123:1806) — elevated
      // background, primary text, elevation-1 shadow. They differ only in which theme's
      // token values that surface resolves against.
      variant: {
        // Follows the global theme, because it adds no scope class of its own and so
        // inherits whichever of `:root` / `.dark` is in effect.
        auto: 'bg-background-elevated text-foreground-primary shadow-elevation-1',
        // Forced light and forced dark. The scope class goes directly on this node (not
        // `dark:`-prefixed) because `@custom-variant dark` is `&:is(.dark *)` and does not
        // match the `.dark` node itself — see DarkThemeScope/DialogContent for the same
        // reasoning. `--background-elevated` is Gray-100 in light and Gray-800 in dark, and
        // `--foreground-primary` inverts to match, so each variant gets the design's own
        // colours for that theme without hardcoding any.
        light:
          'light bg-background-elevated text-foreground-primary shadow-elevation-1 [color-scheme:light]',
        dark: 'dark bg-background-elevated text-foreground-primary shadow-elevation-1 [color-scheme:dark]',
        // The opposite of the global theme: a dark pill in light mode and a light pill in
        // dark mode. Named for that relationship rather than for a fixed colour, because
        // `--foreground` flips with the theme. This is why it is not named `dark` — inside a
        // permanently-dark surface (the player) it would render an inverted (white) pill.
        inverse: 'bg-foreground text-background',
      },
    },
    defaultVariants: {
      variant: 'auto',
    },
  },
);

export type TooltipContentProps = ComponentProps<typeof TooltipPrimitive.Content> &
  VariantProps<typeof tooltipContentVariants> & {
    /**
     * Where the tooltip portals to. Defaults to the `PortalContainerProvider`
     * target, and then to `document.body`.
     */
    container?: ComponentProps<typeof TooltipPrimitive.Portal>['container'];
  };

export const TooltipContent: FC<TooltipContentProps> = ({
  className,
  variant = 'auto',
  sideOffset = 0,
  container,
  children,
  ...props
}) => {
  const providedContainer = usePortalContainer();

  return (
    <TooltipPrimitive.Portal container={container ?? providedContainer}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(tooltipContentVariants({ variant }), className)}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={cn(
            'z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]',
            // Reads --background-elevated via ordinary CSS custom property inheritance from
            // Content's own `.light` / `.dark` (when present), so those variants need no
            // branch of their own here — only `inverse`'s different token does.
            variant === 'inverse'
              ? 'bg-foreground fill-foreground'
              : 'bg-background-elevated fill-background-elevated',
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
};
